import {
  BarChart3,
  ClipboardCheck,
  LayoutGrid,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { SheepLogo } from '@/components/logo';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/alunos', label: 'Alunos', shortLabel: 'Alunos', icon: Users },
  { to: '/turmas', label: 'Turmas', shortLabel: 'Turmas', icon: LayoutGrid },
  { to: '/presenca', label: 'Presença', shortLabel: 'Presença', icon: ClipboardCheck },
  { to: '/painel', label: 'Painel Admin', shortLabel: 'Painel', icon: BarChart3 },
  {
    to: '/professores',
    label: 'Professores',
    shortLabel: 'Professores',
    icon: UserCog,
    adminOnly: true,
  },
];

export function AppShell() {
  const { logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const navItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-dvh">
      {/* Sidebar desktop */}
      <aside className="sticky top-0 hidden h-dvh w-[232px] shrink-0 flex-col gap-1 border-r border-border bg-surface px-3.5 py-5 lg:flex">
        <div className="flex items-center gap-2.5 px-2.5 pb-[22px] pt-1.5">
          <SheepLogo size={28} />
          <div className="text-base font-extrabold tracking-[-0.02em]">SheepCount</div>
        </div>
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                'flex w-full items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-primary-tint font-bold text-primary-dark'
                  : 'font-medium text-fg-soft hover:bg-chip',
              )
            }
          >
            <Icon size={17} />
            {label}
          </NavLink>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3.5 py-2.5 text-left text-[13px] font-medium text-muted transition-colors hover:bg-chip"
        >
          Sair
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar mobile */}
        <header className="sticky top-0 z-20 flex items-center gap-2.5 border-b border-border bg-surface px-[18px] pb-3.5 pt-[calc(14px_+_env(safe-area-inset-top))] lg:hidden">
          <SheepLogo size={26} />
          <div className="text-base font-extrabold tracking-[-0.02em]">SheepCount</div>
        </header>

        <main className="min-w-0 flex-1 px-4 pb-[calc(96px_+_env(safe-area-inset-bottom))] pt-[18px] lg:px-8 lg:pb-8 lg:pt-7">
          <Outlet />
        </main>

        {/* Tab bar mobile */}
        <nav className="fixed inset-x-0 bottom-0 z-[25] flex justify-around border-t border-border bg-surface px-1.5 pb-[calc(8px_+_env(safe-area-inset-bottom))] pt-2 lg:hidden">
          {navItems.map(({ to, shortLabel, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 flex-col items-center justify-center gap-[3px] px-2 py-1',
                  isActive ? 'text-primary-dark' : 'text-muted',
                )
              }
            >
              <Icon size={20} />
              <span className="text-[10.5px] font-semibold">{shortLabel}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
